import { ProviderTransmissionStatusReportItem } from '../../api';

/**
 * Turns `/provider/report/transmission_status?transmissionStatusType=REJECTED` into the rows the
 * Rejections tab shows.
 *
 * ## The grain is a GROUP, not a claim — and the design draws a claim
 *
 * This is the one place the reworked design cannot be followed as drawn, and it is worth being
 * precise about why rather than quietly shipping something adjacent.
 *
 * Jordan's Rejections tab is a worklist: one row per rejected transmission, with patient, date of
 * service, payer, charge, age and an assignee. The only per-claim source is `/provider/status`, and
 * measured on production 2026-09-30 it cannot back that: `filterCreated` is **ignored entirely** (a
 * one-day window in 2001 returns the same records as no filter), there is no status-type filter, and
 * classifying client-side costs 2000 records / 3.7 MB / 36 s to surface about seventy rejections —
 * without bounding the window or knowing the set is complete.
 *
 * So each row here summarises MANY claims, and the count column says how many. Jordan's v3 settled
 * on exactly that grain — a summary by status message — and dropped the per-claim columns an earlier
 * revision drew (they had shipped disabled here, with this reason).
 *
 * ## The headline: re-cut from the payer's own words, never invented
 *
 * `rejectionHeadline` (below) shows `Code 26 · Entity not found` over the full message — only where
 * the payer wrote the standard `Code N - Description.` form, and then in the payer's own words up to
 * the first full stop; any other message shows its own first 64 characters. The full message is
 * always right under it.
 *
 * An earlier version derived the label from the PUBLISHED status-code list instead, and was removed
 * (Jordan agreed, 2026-09-30): for `Code 21`, the commonest code, the official text is the generic
 * *"Missing or invalid information…"* while the payer's line said what was actually wrong. v3's
 * headline cuts the payer's text, not the dictionary's, which avoids exactly that.
 *
 * `statusCode` is still parsed: it is a real, stable identifier, and the editor offers `Code N` as the
 * broader phrase to match on.
 */
export interface IRejectionRow {
    /** Stable across loads: the message is the only identity the aggregate offers. */
    key: string;
    /** The X12 status code, when the message carries one. `21`, `544`. */
    statusCode?: string;
    /** The message exactly as the payer or clearinghouse sent it. This is what the row shows. */
    message: string;
    /** How many rejected transmissions this row summarises. */
    claims: number;
    /** Total charge across them. See the note on the model: charges can overlap ACROSS messages. */
    charge: number;
    /** The transmission status type the row was asked for, when the table shows more than one. */
    statusType?: string;
}

/** `Code 21 - Missing or invalid information.` — the standard shape, and the only one we derive from. */
const CODED_MESSAGE = /^\s*Code\s+([A-Z0-9]{1,4})\s*-\s*(.*)$/i;

const amountOf = (value: ProviderTransmissionStatusReportItem['chargeAmount']): number => {
    const raw = value && (value as { amount?: string }).amount;
    const parsed = Number(raw);

    return Number.isFinite(parsed) ? parsed : 0;
};

export const buildRejectionRows = (
    items: ProviderTransmissionStatusReportItem[] | undefined,
    statusType?: string,
): IRejectionRow[] => {
    const rows: IRejectionRow[] = [];

    (items || []).forEach(item => {
        const message = (item.message || '').trim();
        if (!message) {
            // A blank message is a real row in the report — measured, 739 of them in one window — but
            // it names nothing and cannot be worked. Counting it would put a row on screen that no
            // biller can act on; dropping it silently would make the totals disagree with the tab's
            // own count, so it is neither: see `blankRejections` below.
            return;
        }

        const coded = CODED_MESSAGE.exec(message);

        rows.push({
            key: message,
            statusCode: coded ? coded[1].toUpperCase() : undefined,
            message,
            claims: item.count || 0,
            charge: amountOf(item.chargeAmount),
            ...(statusType ? { statusType } : {}),
        });
    });

    // Most claims first: the row worth working is the one hitting the most claims, and the charge
    // total cannot be used for ordering because charges overlap across messages.
    rows.sort((a, b) => b.claims - a.claims || a.message.localeCompare(b.message));

    return rows;
};

/**
 * Rejections the report returned with no message at all.
 *
 * Reported beside the table rather than folded into it, for the same reason `skipped` is on the
 * denials rows: a table whose total does not match the tab's count, with no hint why, is worse than
 * one that says what it left out (NS-3).
 */
export const blankRejections = (items: ProviderTransmissionStatusReportItem[] | undefined): number =>
    (items || []).filter(item => !(item.message || '').trim()).reduce(
        (total, item) => total + (item.count || 0),
        0,
    );

/** The rows matching a search over the raw message or the status code. */
export const searchRejectionRows = (rows: IRejectionRow[], query: string): IRejectionRow[] => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return rows;
    }

    return rows.filter(
        row =>
            row.message.toLowerCase().includes(needle) ||
            (row.statusCode || '').toLowerCase() === needle,
    );
};

/**
 * The bold line over a rejection message (FE-116, Jordan's v3): `Code 26 · Entity not found`.
 *
 * DERIVED from the message, never invented — only where the payer wrote the standard
 * `Code N - Description.` form, and then in the payer's own words up to the first full stop. Any
 * other message shows its own first 64 characters. v2 removed an earlier headline that guessed at
 * meanings ("good catch", Jordan, 2026-09-30); this one only re-cuts text that is already there.
 */
export const rejectionHeadline = (message: string): string => {
    const coded = /Code (\d+) - ([^.]+)/.exec(message);
    if (coded) {
        return `Code ${coded[1]} \u00b7 ${coded[2].trim()}`;
    }
    const text = message.trim();
    return text.length > 64 ? `${text.slice(0, 61)}\u2026` : text;
};
